import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import { ProfileHeader } from "./ProfileHeader"
import DateSelect from "components/DateSelect/temp3"
import DataTable from "components/DataTable"
import ReactPaginate from "react-paginate"

import {
  useLazyGetListLeaveQuery,
  useLazyGetLeaveSummaryQuery,
  useLazyGetListDetailQuery,
  useUploadFileMutation,
  useUpdateLeaveFormMutation,
} from "shared/redux/endpoints/employee"
import {
  useCreateLeaveRequestMutation,
  useLazyGetLeaveTypesQuery,
} from "shared/redux/endpoints/leaveRequest"

import { useEffect, useMemo, useState } from "react"
import MDSelect from "components/MDSelect"
import moment from "moment"
import { enUS } from "date-fns/locale"

import { ReactComponent as DocsIcon } from "assets/images/icons/docs.svg"
import { ReactComponent as TimeIcon } from "assets/images/icons/times.svg"
import { ReactComponent as InfoIcon } from "assets/images/icons/info.svg"
import { ReactComponent as SearchIcon } from "assets/images/icons/search.svg"
import { ReactComponent as EditIcon } from "assets/images/icons/edit.svg"

import {
  pickUploadFiles,
  mapUploadedToDtoAttachments,
  mapExistingUrlToDtoAttachments,
} from "shared/utils/common"

import "./LeaveSystem.scss"
import ModalLeaveApplication from "components/ModalLeaveApplication/ModalLeaveApplication"
import MDSnackbar from "components/MDSnackbar"
import { useTreasureHunt } from "components/useTreasureHunt/useTreasureHunt"

export const LeaveSystem = () => {
  const startOfMonth = moment().startOf("month").format("YYYY-MM-DD")
  const endOfRange = moment()
    .startOf("month")
    .add(60, "days")
    .format("YYYY-MM-DD")

  const [pagination, setPagination] = useState({ totalPage: 0, currentPage: 0 })
  const { triggerIfNew } = useTreasureHunt("myself-open-leave-modal")

  const [triggerDetailLeave] = useLazyGetListDetailQuery()
  const [getListLeave, { isLoading: isLoadingLeave }] =
    useLazyGetListLeaveQuery()
  const [getLeaveSummary] = useLazyGetLeaveSummaryQuery()

  const [uploadFile] = useUploadFileMutation()
  const [updateLeaveForm] = useUpdateLeaveFormMutation()
  const [createLeaveRequest] = useCreateLeaveRequestMutation()
  const [getLeaveTypes] = useLazyGetLeaveTypesQuery()

  const [leaveTypeOptions, setLeaveTypeOptions] = useState([
    { value: "", label: "All Types", name: "leaveType" },
  ])

  const [openModalLeave, setOpenModalLeave] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  // Filter state for the list
  const [state, setState] = useState({
    page: 0,
    size: 10,
    startDate: startOfMonth,
    endDate: endOfRange,
    leaveType: "",
    status: "",
  })

  const [leaveSummary, setLeaveSummary] = useState(null)
  const [dataView, setDataView] = useState({})

  const [dataTable, setDataTable] = useState({
    columns: [
      { Header: "No", accessor: "no", width: "50px" },
      { Header: "Submit Date", accessor: "createdAt", width: "200px" },
      { Header: "Leave Type", accessor: "leaveType", width: "100px" },
      { Header: "Leave Dates", accessor: "leaveDates", width: "250px" },
      { Header: "Total Leave Number", accessor: "totalDays", width: "150px" },
      { Header: "Status", accessor: "status", width: "150px" },
      { Header: "Reason", accessor: "reason", width: "20%" },
      { Header: "Approver", accessor: "approver", width: "120px" },
      { Header: "Approved Date", accessor: "approvedDate", width: "120px" },
      { Header: "Action", accessor: "action", width: "100px", sticky: "right" },
    ],
    rows: [],
  })

  const optionsStatus = useMemo(
    () => [
      { value: "APPROVED", label: "Approved", name: "status" },
      { value: "PENDING", label: "Pending", name: "status" },
      { value: "REJECTED", label: "Rejected", name: "status" },
      { value: "DRAFT", label: "Draft", name: "status" },
    ],
    []
  )

  const handleDataTable = (list = [], params = state) => {
    const safeList = Array.isArray(list) ? list : []
    setDataTable((prev) => ({
      ...prev,
      rows: safeList.map((item, index) => {
        const statusLower = (item?.status ?? "").toString().toLowerCase()
        const createdAt = item?.createdAt
          ? moment(item.createdAt).format("DD/MM/YYYY HH:mm")
          : "-"
        const approvedDate = item?.updatedAt
          ? moment(item.updatedAt).format("DD/MM/YYYY HH:mm")
          : "-"

        const start = item?.startDate ?? "-"
        const end = item?.endDate ?? "-"

        return {
          ...item,
          no: params.page * params.size + index + 1,
          leaveDates: `${start} - ${end}`,
          createdAt,
          approvedDate,
          status: (
            <MDTypography
              className={`chip-status chip-status-${statusLower || "unknown"}`}
            >
              {statusLower || "-"}
            </MDTypography>
          ),
          action: (
            <MDTypography
              className="action-link"
              onClick={() => handleClickView(item)}
            >
              <EditIcon />
            </MDTypography>
          ),
        }
      }),
    }))
  }

  const fetchLeaveSummary = async () => {
    const response = await getLeaveSummary()
    if (response?.data?.status) {
      setLeaveSummary(response.data.data)
    }
  }

  const fetchList = async (params) => {
    const response = await getListLeave(params)
    const result = response?.data?.data?.data ?? []
    const totalPage = response?.data?.data?.totalPages ?? 0

    setPagination({ totalPage, currentPage: params.page })
    handleDataTable(result, params)
  }

  const handleClickView = (item) => {
    // view/edit existing request
    setIsNew(false)
    triggerDetailLeave(item.leaveRequestId).then((res) => {
      if (res?.data?.status) {
        setDataView(res.data.data)
        setOpenModalLeave(true)
      }
    })
  }

  const handleChangePage = (page) => {
    const next = { ...state, page }
    setState(next)
    fetchList(next)
  }

  const handleSearch = () => {
    const next = { ...state, page: 0 }
    setState(next)
    fetchList(next)
  }

  // Accept both "event-like" and "option-like" onChange payloads
  const onChangeSelect = (payload) => {
    // event: payload.target.name/value

    if (payload?.target?.[name]) {
      const { name, value } = payload.target
      setState((prev) => ({ ...prev, [name]: value }))
      fetchList({ ...state, [name]: value, page: 0 })
      return
    } else {
      const name = payload?.name
      const value = payload?.value
      if (name) setState((prev) => ({ ...prev, [name]: value }))
      fetchList({ ...state, [name]: value, page: 0 })
    }
  }

  useEffect(() => {
    fetchLeaveSummary()
    getLeaveTypes().then((res) => {
      if (res?.data?.data) {
        const types = res.data.data.map((item) => ({
          value: item.code,
          label: item.nameEn,
          name: "leaveType",
        }))
        setLeaveTypeOptions([{ value: "", label: "All Types", name: "leaveType" }, ...types])
      }
    })
  }, [])

  useEffect(() => {
    // initial list load
    fetchList(state)
  }, [])

  async function submitLeave(form) {
    const selectedFiles = Array.isArray(form?.attachments)
      ? form.attachments
      : []

    // 1) Upload nếu có File mới
    const filesToUpload = pickUploadFiles(selectedFiles)

    let uploadedRes = null
    if (filesToUpload.length > 0) {
      const formData = new FormData()
      filesToUpload.forEach((file) => formData.append("attachments", file))

      uploadedRes = await uploadFile(formData)
    }

    // Normalize upload response for mapping util

    // 2) Map attachments vào dto
    const attachments = [
      ...mapExistingUrlToDtoAttachments(selectedFiles),
      ...mapUploadedToDtoAttachments(uploadedRes),
    ]

    // 3) Dedup theo url
    const seen = new Set()
    const dedupedAttachments = attachments.filter((a) => {
      const key = a?.url
      if (!key) return false
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })

    // 4) Build dto submit
    const dto = {
      leaveType: form?.leaveType?.value,
      startDate: moment(form?.fromDate).format("YYYY-MM-DD"),
      endDate: moment(form?.toDate).format("YYYY-MM-DD"),
      dayType: form?.isHalfDay ? "HALF_DAY" : "FULL_DAY",
      reason: form?.reason ?? "Medical leave",
      approverId: form?.approver?.userId || form?.approver?.id,
      notificationRecipientIds: form?.notifyTo?.map((u) => u.id) ?? [],
      uploadedAttachments: dedupedAttachments,
      ...(isNew ? {} : { id: dataView?.leaveRequestId }),
    }

    try {
      const res = isNew
        ? await createLeaveRequest(dto)
        : await updateLeaveForm(dto)

      if (res.error) {
        setOpenSnackbar({
          open: true,
          severity: "error",
          message: res.error?.data?.data?.message,
        })
      } else {
        setOpenSnackbar({
          open: true,
          severity: "success",
          message: isNew
            ? "Leave request created successfully"
            : "Leave request updated successfully",
        })
        await fetchList(state)
        return res
      }
      // refresh list after submit
    } catch (e) {
      console.log(e)
    }
  }

  const renderList = [
    {
      label: "Approved Leave",
      subTitle: `${leaveSummary?.approvedLeaveDays ?? "0"} days`,
      icon: <DocsIcon />,
    },
    {
      label: "Pending Approval Leave",
      subTitle: `${leaveSummary?.pendingLeaveDays ?? "0"} days`,
      icon: <TimeIcon />,
    },
    {
      label: "Leave Information",
      subTitle: (
        <MDBox>
          <MDTypography fontSize="14px">
            Taken Leave: <b>{leaveSummary?.leaveInfo?.taken ?? "0"} days</b>
          </MDTypography>
          <MDTypography fontSize="14px">
            Upcoming Leave:{" "}
            <b>{leaveSummary?.leaveInfo?.upcoming ?? "0"} days</b>
          </MDTypography>
          <MDTypography fontSize="14px">
            Remaining Leave:{" "}
            <b>{leaveSummary?.leaveInfo?.remaining ?? "0"} days</b>
          </MDTypography>
        </MDBox>
      ),
      icon: <InfoIcon />,
    },
  ]

  const propsModalLeaveApplication = {
    isOpen: openModalLeave,
    onClose: () => {
      setOpenModalLeave(false)
      setIsNew(false)
    },
    onSubmit: (payload) => {
      submitLeave(payload)
      setOpenModalLeave(false)
    },
    isEdit: !isNew,
    dataEdit: dataView,
  }

  return (
    <MDBox customClass="c-leave-system" className="myself-leave-system">
      <MDBox>
        <MDBox mt={2} mb={3}>
          <ProfileHeader>
            <MDBox
              ml="auto"
              className="c-leave-system--submit large"
              onClick={() => {
                triggerIfNew()
                setDataView({})
                setIsNew(true)
                setOpenModalLeave(true)
              }}
            >
              Submit Leave
            </MDBox>
          </ProfileHeader>
        </MDBox>

        <MDBox LeaveDashboard className="c-leave-system--dashboard">
          <MDTypography className="c-leave-system--title">
            Leave Dashboard
          </MDTypography>

          <MDBox
            display="flex"
            gap={2}
            mt={2}
            alignItems="stretch"
            width="100%"
            className="c-leave-system--summary"
          >
            {renderList.map((item) => (
              <MDBox
                display="flex"
                alignItems="center"
                gap="20px"
                flex={1}
                className="c-leave-system--item"
                key={item.label}
              >
                <MDBox className="c-leave-system--icon">{item.icon}</MDBox>
                <MDBox>
                  <MDTypography className="c-leave-system--subtitle">
                    {item.label}
                  </MDTypography>
                  <MDTypography
                    className="c-leave-system--subtitle"
                    fontSize="14px"
                  >
                    {item.subTitle}
                  </MDTypography>
                </MDBox>
              </MDBox>
            ))}
          </MDBox>
        </MDBox>

        <MDBox
          TableLeaveInformation
          mt={2}
          className="c-leave-system--dashboard c-leave-system--table-information"
        >
          <MDTypography mb={2} className="c-leave-system--title">
            Leave Information Details
          </MDTypography>

          <MDBox display="flex" gap={2} mb={2} alignItems="center">
            <MDSelect
              placeholder="Leave Type"
              options={leaveTypeOptions}
              onChange={onChangeSelect}
              name="leaveType"
            />

            <MDSelect
              placeholder="Status"
              options={optionsStatus}
              onChange={onChangeSelect}
              name="status"
            />

            <DateSelect
              onChangeStartDate={(e) =>
                setState((prev) => ({
                  ...prev,
                  startDate: moment(e).format("YYYY-MM-DD"),
                }))
              }
              onChangeEndDate={(e) =>
                setState((prev) => ({
                  ...prev,
                  endDate: moment(e).format("YYYY-MM-DD"),
                }))
              }
              defaultStartDate={state.startDate}
              defaultEndDate={state.endDate}
              locale={enUS}
            />

            <MDBox
              className="c-leave-system--submit small"
              onClick={handleSearch}
            >
              <MDTypography color="white" fontSize="12px">
                Search
              </MDTypography>
              <SearchIcon />
            </MDBox>
          </MDBox>

          <MDBox className="teacher-management__table">
            <MDBox className="teacher-management__table-wrapper">
              <DataTable
                table={dataTable}
                noEndBorder
                className="teacher-management__table"
              />

              <MDBox className="table-footer">
                <MDBox className="table-footer-showing">
                  Showing {dataTable?.rows?.length ?? 0} entries
                  {isLoadingLeave ? " (loading...)" : ""}
                </MDBox>

                {pagination.totalPage > 1 && (
                  <ReactPaginate
                    className="pagination"
                    nextLabel={<Icon>chevron_right</Icon>}
                    previousLabel={<Icon>chevron_left</Icon>}
                    forcePage={Number(state.page)}
                    onPageChange={(event) => handleChangePage(event.selected)}
                    pageCount={pagination.totalPage}
                  />
                )}
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>

      <ModalLeaveApplication {...propsModalLeaveApplication} />
      <MDSnackbar
        color={openSnackbar.severity}
        icon="check"
        title="Notice"
        content={openSnackbar.message}
        open={openSnackbar.open}
        onClose={() => setOpenSnackbar({ ...openSnackbar, open: false })}
        close={() => setOpenSnackbar({ ...openSnackbar, open: false })}
        bgWhite
      />
    </MDBox>
  )
}
